// streaming STT (spec: docs/superpowers/specs/2026-07-07-m3-continuous-conversation-requirements.md §3.12 / §4.1 Q2):
// ElevenLabs Scribe v2 Realtime WebSocket client。
// 1 発話 = 1 WS session。partial_transcript を live captions として UI に流し、
// committed_transcript で確定。判別フィールドは `message_type`。
// 参考: https://elevenlabs.io/docs/api-reference/speech-to-text/v-1-speech-to-text-realtime
//
// R2 log expansion (spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §6.2 / §7.3):
//   WSS lifecycle (wss.connected / wss.disconnected / wss.error) と
//   scribe.partial (1s throttle + 2 KB UTF-8 text truncate) / scribe.final を eventBus に emit する。
//   opts.sessionId 未指定なら useChatStore.getState().sessionId にフォールバックする
//   (Task 6 の mediaStreamManager / vadController / silenceTimer と同じ resolution パターン)。
//   両方 unset の場合のみ emit を skip する。

import { eventBus } from "../observability/eventBus";
import { makeEvent } from "../observability/eventSchema";
import { useChatStore } from "../state/chatStore";
import { logMetric, estimateSttCostUsd } from "../observability/metrics";

// 診断ログ (?scribe-debug=1)。WS の open/close/送信/受信を console に出して、
// 「VAD は検知するのに文字起こしが返らない」系の切り分けに使う (本番は既定 off で無音)。
const SCRIBE_DEBUG =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("scribe-debug") === "1";
function slog(...args: unknown[]): void {
  if (SCRIBE_DEBUG) console.info("[scribe]", ...args);
}

type Opts = {
  /**
   * 未指定なら useChatStore.getState().sessionId にフォールバックして emit する
   * (Task 8 の useContinuousMode は sessionId を渡さずに構築するため)。
   * 両方 unset なら event emit を skip (既存 test 後方互換 + fallback 経路の防衛)。
   */
  sessionId?: string;
  /** performance.now() を test で置き換えるための DI */
  now?: () => number;
  /** realtime STT の背景音フィルタ (ticket 2026-07-21)。既定 false。呼び出し側が env で切替える。 */
  filterBackgroundAudio?: boolean;
  onPartial: (text: string) => void;
  /**
   * 確定文が届いた。`meta.continuation` が true なら**直前の確定文と同じ発話の続き**
   * (1 発話が複数の確定文に割れる、2026-08-15 実測)。共通型
   * `StreamingSttClientOpts` (src/api/streamingSttClient.ts) と同じ形を保つこと。
   */
  onCommitted: (text: string, meta?: { continuation: boolean }) => void;
  onError?: (e: unknown) => void;
};

type TokenResponse = { token: string; wsUrl: string; expiresAt: string };

type ServerMessage =
  | { message_type: "session_started" }
  | { message_type: "partial_transcript"; text: string }
  | { message_type: "committed_transcript"; text: string }
  | {
      message_type: "committed_transcript_with_timestamps";
      text: string;
      language_code?: string;
      words?: unknown[];
    }
  | {
      message_type: "error" | "auth_error" | "quota_exceeded" | "rate_limited";
      [k: string]: unknown;
    };

const SAMPLE_RATE = 16000;
const KEYTERM_MAX_LEN = 50;
/**
 * 1 通の `input_audio_chunk` に載せる最大サンプル数 = 1 秒。
 *
 * **上限は 1 通あたり 30.000 秒。実 API へのプローブで確定させた** (2026-08-11):
 * 480,000 samples (30.000 秒) は通り、**480,001 samples で `chunk_size_exceeded`**。
 * ElevenLabs のドキュメントには閾値が書かれていない (エラー型
 * `ScribeChunkSizeExceededError` の存在だけが載っている) ため実測した。
 * **バイト数ではなく音声長の上限**で、`commit` までの累積ではなく 1 通ごとに掛かる
 * (1.63 MB を 40 通に分けて commit 前に送っても通ることを実測)。
 *
 * ここを 1 秒 = **上限の 30 分の 1** に置くのは、大きくする利点が無い一方で
 * 送信中に WS が切れたときの取りこぼし窓が小さくなるため。
 * 計測: docs/measurements/2026-08-11-stt-chunk-size-limit.md
 * チケット: docs/tickets/2026-08-11-stt-chunk-size-exceeded.md
 */
const AUDIO_CHUNK_SAMPLES = SAMPLE_RATE;
/**
 * 実測した上限そのもの (16 kHz で 30 秒)。`AUDIO_CHUNK_SAMPLES` を将来
 * 引き上げるときに、ここを超えていないことをテストが番人として見る。
 */
export const SCRIBE_MAX_CHUNK_SAMPLES = SAMPLE_RATE * 30;
const PARTIAL_THROTTLE_MS = 1000;
// spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §5.2 / §6.3:
// payload.text は 2 KB **UTF-8 bytes** で truncate (UTF-16 code units ではない)。
// 日本語は ~3 bytes/char なので char-length cap を使うと ~3x 過剰になる (2026-07-11 review fix)。
const SCRIBE_TEXT_HARD_CAP_BYTES = 2048;
const WSS_ERROR_MSG_CAP = 500;

type WssErrorPhase = "token" | "connect" | "session" | "close";

export class StreamingScribeClient {
  private ws: WebSocket | null = null;
  private opts: Opts;

  // metric の `model` に載せる名前。**client は `model_id` を送っていない** —
  // realtime endpoint が何を返すかは token minter 側の設定に従う。ここはその写しなので、
  // minter のモデルを変えたらここも変える (ズレると集計のモデル別内訳だけが静かに嘘になる)。
  private static readonly MODEL = "scribe_v2_realtime";
  // R2 log expansion 用 internal state (spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §7.3)
  private streamStartedAt: number | null = null;
  private lastPartialEmitMs = 0;
  private lastPartialText = "";
  private explicitCloseRequested = false;
  private currentTurnIndex = 0;
  private currentTurnId: string | undefined;
  private currentUtteranceIndex = 0;
  // commit() 呼び出し時刻 (= 発話終端の合図)。次の committed_transcript が届いた時点で
  // 差分を取り scribe.final.latencyFromSpeechEndMs として emit し、その後 null に戻す
  // (1 utterance = 1 commit の前提。commit なしで届いた committed_transcript は対象外)。
  private lastCommitRequestedAt: number | null = null;
  // 確定文を受け取ってから、次の音声を送るまでの間か。**ElevenLabs は
  // committed_transcript の直後に、同じテキストの partial_transcript をもう一度送ってくる**
  // (2026-08-04 実機ログで実測)。その partial に対応する確定文はもう来ないので、
  // 確定文の到着でしか途中経過を消せない呼び出し側では消しようがなく、消したばかりの
  // 薄い吹き出しが復活して残り続ける (設問が進むと新しい設問の下へ付いてくる)。
  // この窓の partial は同じ発話の残響なので落とす — 次の発話は sendAudio から始まる。
  private committedSinceLastAudio = false;
  // commit までに**実際に送れた**サンプル数 (metric の audioDurationMs の材料)。
  // 渡された数ではない — 送信中に切断されると途中で打ち切るため。
  private audioSamplesSinceCommit = 0;

  constructor(opts: Opts) {
    this.opts = opts;
  }

  /**
   * turn / utterance context を上書き。ChatScreen / useContinuousMode が
   * connect() 前後で呼び、scribe.partial / scribe.final の payload に反映する。
   * Task 8 で caller 側から呼び出される。
   */
  setTurnContext(args: { turnIndex: number; turnId?: string; utteranceIndex: number }): void {
    this.currentTurnIndex = args.turnIndex;
    this.currentTurnId = args.turnId;
    this.currentUtteranceIndex = args.utteranceIndex;
  }

  private now(): number {
    return this.opts.now ? this.opts.now() : performance.now();
  }

  private elapsedMs(): number {
    return this.streamStartedAt === null ? 0 : this.now() - this.streamStartedAt;
  }

  async connect(keyterms: string[]): Promise<void> {
    // 前回 session 分の internal state を毎回 reset
    this.explicitCloseRequested = false;
    this.streamStartedAt = null;
    this.lastPartialEmitMs = 0;
    this.lastPartialText = "";
    this.lastCommitRequestedAt = null;
    // **旗も降ろす** (2026-08-15)。降ろさないと、旧 client で確定文を受けたまま idle 切断され、
    // 新 client の最初の確定文が「前の発話の続き」を名乗る。60 秒級の発話では WS が必ず
    // 張り直される (サーバが約 15 秒で切る) ので現実に踏む。OpenAI 版は :164 で
    // completedItems.clear() を既にしており、こちらだけ非対称だった。
    this.committedSinceLastAudio = false;

    let tokenRes: Response;
    try {
      tokenRes = await fetch("/api/stt/token", { method: "POST" });
    } catch (e) {
      const err = e as Error;
      this.emitWssError("token", {
        name: err?.name ?? "TokenFetchError",
        message: (err?.message ?? "").slice(0, WSS_ERROR_MSG_CAP),
      });
      throw e;
    }
    if (!tokenRes.ok) {
      this.emitWssError("token", {
        httpStatus: tokenRes.status,
        name: "TokenFetchFailed",
        message: `token fetch failed: ${tokenRes.status}`.slice(0, WSS_ERROR_MSG_CAP),
      });
      throw new Error(`token fetch failed: ${tokenRes.status}`);
    }
    const { token, wsUrl } = (await tokenRes.json()) as TokenResponse;

    const params = new URLSearchParams();
    params.set("token", token);
    params.set("language_code", "ja");
    // pcm_16000 = 16 kHz signed 16-bit little-endian mono, sendAudio() の変換と揃える
    params.set("audio_format", "pcm_16000");
    // VAD が発話境界を判定するため server 側 vad ではなく manual commit を採用
    params.set("commit_strategy", "manual");
    // 背景音フィルタ (ticket 2026-07-21)。既定 false のときは付けない (上流の既定に委ねる)。
    if (this.opts.filterBackgroundAudio) {
      params.set("filter_background_audio", "true");
    }
    for (const t of keyterms.filter((s) => s.length > 0 && s.length <= KEYTERM_MAX_LEN)) {
      params.append("keyterms", t);
    }
    const url = `${wsUrl}?${params.toString()}`;

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      this.ws = ws;
      ws.onopen = () => {
        this.streamStartedAt = this.now();
        this.emitEvent("wss.connected", { tsMs: 0 });
        slog("ws open", { keyterms: keyterms.length });
        resolve();
      };
      ws.onerror = (e) => {
        const err = e as unknown as { message?: string };
        this.emitWssError("connect", {
          name: "WebSocketError",
          message: (err?.message ?? "").slice(0, WSS_ERROR_MSG_CAP),
        });
        this.opts.onError?.(e);
        reject(new Error("WS error"));
      };
      ws.onclose = (ev: CloseEvent) => {
        const tsMs = this.elapsedMs();
        slog("ws CLOSED", {
          code: ev.code,
          reason: ev.reason,
          wasClean: ev.wasClean,
          elapsedMs: Math.round(tsMs),
          initiator: this.explicitCloseRequested ? "client" : "server",
        });
        this.emitEvent("wss.disconnected", {
          tsMs,
          code: ev.code,
          reason: ev.reason,
          wasClean: ev.wasClean,
          initiator: this.explicitCloseRequested ? "client" : "server",
        });
        // **理由が付いた切断は、clean でも異常として記録する** (2026-08-11 の実測)。
        // `chunk_size_exceeded` は `code=1000 / wasClean=true` で届くため、
        // 従来の `!wasClean || code !== 1000` では**正常な切断として素通りしていた** —
        // サーバが理由を明示しているのに events.jsonl には「正常に切れた」としか残らず、
        // 回答が消えた原因が後から追えなかった。
        //
        // 判定を「理由の有無」に置く根拠は本番ログ: **166 件の切断すべてが `reason: ""`**
        // だった (誤検知ゼロ)。こちらから閉じる `close()` も引数なしなので理由は付かない。
        //
        // **名前を分ける**のは、`wss.error` が既にノイズを多く含むため
        // (社内パイロットでは 76 件中 67 件が正常なアイドル切断)。同じ名前にすると
        // 「サーバがデータを明示的に拒否した」という最も重い信号が埋もれる。
        const rejected = ev.reason !== "";
        if (rejected || !ev.wasClean || ev.code !== 1000) {
          this.emitWssError("close", {
            name: rejected ? "ServerRejected" : "AbnormalClose",
            message: `code=${ev.code} reason=${ev.reason}`.slice(0, WSS_ERROR_MSG_CAP),
          });
        }
      };
      ws.onmessage = (ev) => {
        const evData = typeof ev.data === "string" ? ev.data : "";
        let msg: ServerMessage;
        try {
          msg = JSON.parse(evData) as ServerMessage;
        } catch {
          return;
        }
        switch (msg.message_type) {
          case "partial_transcript": {
            // 確定済みの発話に対する残響 (上の committedSinceLastAudio を参照)。
            // 通すと呼び出し側の live captions が復活し、次の確定文が来ないまま残る。
            if (this.committedSinceLastAudio) break;
            const now = this.now();
            const changed = msg.text !== this.lastPartialText;
            const elapsed = now - this.lastPartialEmitMs;
            if (changed || elapsed >= PARTIAL_THROTTLE_MS) {
              const { text, truncated } = truncateUtf8(msg.text, SCRIBE_TEXT_HARD_CAP_BYTES);
              this.emitEvent("scribe.partial", {
                tsMs: this.elapsedMs(),
                utteranceIndex: this.currentUtteranceIndex,
                turnIndex: this.currentTurnIndex,
                text,
                charCount: msg.text.length,
                ...(truncated ? { _truncated: true } : {}),
              });
              this.lastPartialEmitMs = now;
              this.lastPartialText = msg.text;
            }
            slog("partial:", msg.text);
            this.opts.onPartial(msg.text);
            break;
          }
          case "committed_transcript":
          case "committed_transcript_with_timestamps": {
            const languageCode =
              "language_code" in msg && typeof msg.language_code === "string"
                ? msg.language_code
                : undefined;
            const wordCount =
              "words" in msg && Array.isArray(msg.words) ? msg.words.length : undefined;
            const { text, truncated } = truncateUtf8(msg.text, SCRIBE_TEXT_HARD_CAP_BYTES);
            // commit() 呼び出し (= 発話終端) から今回の確定までの latency。
            // commit() を経由していない committed_transcript (現行 manual commit_strategy では
            // 想定外だが防御的に) は undefined のまま payload に含めない。
            const latencyFromSpeechEndMs =
              this.lastCommitRequestedAt !== null
                ? this.now() - this.lastCommitRequestedAt
                : undefined;
            this.lastCommitRequestedAt = null;
            // **`= true` を立てる前に読む** — 立てた後だと 1 件目まで継続を名乗る。
            // このフラグは確定文の到着で立ち `sendAudio()` でしか降りない (:334)。
            // `sendAudio` を呼ぶのは `onSpeechEnd` だけなので、
            // **立っている間に届いた確定文 = 同じ発話の続き**が成立する。
            // 残響 partial を捨てる窓 (:250) と物理的に同じフラグなので、
            // 「発話の境界」の定義がアプリ内に 2 つ生まれない (タイマーも閾値も要らない)。
            const continuation = this.committedSinceLastAudio;
            this.emitEvent("scribe.final", {
              tsMs: this.elapsedMs(),
              utteranceIndex: this.currentUtteranceIndex,
              turnIndex: this.currentTurnIndex,
              text,
              charCount: msg.text.length,
              ...(languageCode ? { languageCode } : {}),
              ...(wordCount !== undefined ? { wordCount } : {}),
              ...(truncated ? { _truncated: true } : {}),
              ...(latencyFromSpeechEndMs !== undefined ? { latencyFromSpeechEndMs } : {}),
              // 割れた 2 件目であることを R2 に残す。**連結して 1 件にはしない** —
              // scribe.final は CRITICAL_TYPES に入る「発話テキストの正」で、
              // まとめると今回の欠陥を発見できた唯一の材料 (2 件並ぶこと) が消える。
              ...(continuation ? { continuation: true } : {}),
            });
            slog("committed:", msg.text, continuation ? "(continuation)" : "");

            // A4-1: STT の metric はここでしか作れない (レイテンシ・文字数が揃う唯一の点)。
            // これが無いと `summary.json` の `stt` が count: 0 のままになり、
            // **最大費目を「0ms / 0 円」と表示し続ける** (2026-08-05 の全セッションがそうだった)。
            //
            // **音声の長さは 1 通目にだけ載せる。** 1 発話が複数の確定文に割れるのは
            // サーバ側の区切りであって、こちらが送った音声は 1 回ぶんしかない
            // (2026-08-18 実測: 79 秒が 3 通)。通ごとに載せると尺もコストも 3 倍に見える。
            const audioDurationMs = continuation
              ? null
              : Math.round((this.audioSamplesSinceCommit / SAMPLE_RATE) * 1000);
            this.audioSamplesSinceCommit = 0;
            logMetric({
              kind: "stt",
              provider: "elevenlabs",
              recordedAt: Date.now(),
              // **`opts.sessionId` を直読みしない。** 本編 (`useContinuousMode`) は opts に
              // 渡しておらず、client 自身が store から解決している (emitEvent と同じ)。
              // 直読みすると本編の metric だけ sessionId 無しで記録され、
              // `buildTurnMetrics` の session 絞り込みで落ちる (preview 実機で
              // metrics.json に llm と tts はあるのに stt だけ無い形で発覚)。
              sessionId: this.resolveSessionId() ?? undefined,
              audioDurationMs,
              // realtime WS は処理時間を返さないので、サーバ側の内訳は取れない
              serverDurationMs: null,
              // commit (= 発話終端) から確定文が届くまで
              roundTripMs: latencyFromSpeechEndMs ?? 0,
              chars: msg.text.length,
              model: StreamingScribeClient.MODEL,
              estimatedCostUsd: estimateSttCostUsd(audioDurationMs),
            });

            this.committedSinceLastAudio = true;
            this.opts.onCommitted(msg.text, { continuation });
            break;
          }
          case "error":
          case "auth_error":
          case "quota_exceeded":
          case "rate_limited":
            this.emitWssError("session", {
              messageType: msg.message_type,
              name: msg.message_type,
              message: JSON.stringify(msg).slice(0, WSS_ERROR_MSG_CAP),
            });
            this.opts.onError?.(msg);
            break;
          case "session_started":
          default:
            // 無視 (session_started 通知 / 未知 message_type、既存挙動維持)
            break;
        }
      };
    });
  }

  sendAudio(pcm: Float32Array): void {
    if (!this.ws || this.ws.readyState !== 1) {
      slog("sendAudio DROPPED (ws not open)", {
        readyState: this.ws?.readyState ?? "null",
        samples: pcm.length,
      });
      return;
    }
    slog("sendAudio", { samples: pcm.length });
    // 新しい発話が始まった。ここから先の partial は残響ではないので通す。
    this.committedSinceLastAudio = false;
    // **1 通に詰め込まず分割する** (2026-08-11 の preview 実機で実測):
    // 1 通の上限は 30.000 秒で、37 秒の発話を 1 通で送ると `chunk_size_exceeded` で
    // 接続を切られ**回答が丸ごと消える**。上限は 1 通ごとに掛かる (累積ではない) ので
    // 分割すれば長さに制限がなくなる。
    // commit は付けない — 分割ごとに commit すると 1 発話が複数の確定文に割れる。
    // 送信中の切断は現実に起きる (サーバは 15 秒でアイドル切断してくる) ので、
    // 毎回 readyState を見て打ち切る。throw させると onSpeechEnd が途中で止まり
    // commit ごと落ちる。
    for (let i = 0; i < pcm.length; i += AUDIO_CHUNK_SAMPLES) {
      if (this.ws.readyState !== 1) {
        slog("sendAudio TRUNCATED (ws closed mid-send)", { sentSamples: i, total: pcm.length });
        return;
      }
      const chunk = pcm.subarray(i, i + AUDIO_CHUNK_SAMPLES);
      this.audioSamplesSinceCommit += chunk.length;
      this.ws.send(
        JSON.stringify({
          message_type: "input_audio_chunk",
          audio_base_64: floatToBase64Pcm16(chunk),
          commit: false,
          sample_rate: SAMPLE_RATE,
        })
      );
    }
  }

  commit(): void {
    if (!this.ws || this.ws.readyState !== 1) {
      slog("commit DROPPED (ws not open)", { readyState: this.ws?.readyState ?? "null" });
      return;
    }
    slog("commit");
    // 発話終端 timestamp を記録 (scribe.final.latencyFromSpeechEndMs の起点)
    this.lastCommitRequestedAt = this.now();
    // manual commit_strategy: onSpeechEnd 後に空 audio + commit=true で utterance を確定させる
    this.ws.send(
      JSON.stringify({
        message_type: "input_audio_chunk",
        audio_base_64: "",
        commit: true,
        sample_rate: SAMPLE_RATE,
      })
    );
  }

  close(): void {
    // ws.close() の同期発火 onclose 内で initiator=client 判定できるよう
    // 先に flag を立ててから WebSocket を close する (順序が load-bearing)
    this.explicitCloseRequested = true;
    this.ws?.close();
    this.ws = null;
  }

  /**
   * WS が送信可能 (OPEN) かどうか。continuous mode の長寿命 WS は無音中に server 側で
   * アイドル切断されるため、発話送信前に開通確認して張り直すのに使う (実機で判明)。
   */
  isOpen(): boolean {
    return this.ws !== null && this.ws.readyState === 1;
  }

  /**
   * opts.sessionId 優先、未指定なら useChatStore.getState().sessionId にフォールバック
   * (Task 6 の mediaStreamManager / vadController / silenceTimer と同じ resolution パターン)。
   * Task 8 の useContinuousMode は sessionId を渡さずに StreamingScribeClient を構築するため、
   * これが無いと production で全 emit が dead code になる (2026-07-11 review fix)。
   */
  private resolveSessionId(): string | null {
    return this.opts.sessionId ?? useChatStore.getState().sessionId;
  }

  private emitEvent(type: string, payload: Record<string, unknown>): void {
    const sessionId = this.resolveSessionId();
    if (!sessionId) return;
    eventBus.emit(makeEvent(type, sessionId, payload, this.currentTurnId));
  }

  private emitWssError(
    phase: WssErrorPhase,
    detail: { name: string; message: string; httpStatus?: number; messageType?: string }
  ): void {
    const sessionId = this.resolveSessionId();
    if (!sessionId) return;
    eventBus.emit(makeEvent("wss.error", sessionId, { phase, ...detail }, this.currentTurnId));
  }
}

/**
 * text を maxBytes **UTF-8 bytes** で truncate する
 * (spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §5.2 / §6.3 の 2 KB hard cap)。
 * `text.length` (UTF-16 code units) ではなく encoded byte length で判定する点が重要:
 * 日本語は ~3 bytes/char のため char-length cap だと ~3x 過剰になる。
 * バイト境界で切ると末尾がマルチバイト文字の途中になり得るため、TextDecoder が
 * 挿入する末尾の U+FFFD (replacement character) を trim してから返す。
 */
export function truncateUtf8(text: string, maxBytes: number): { text: string; truncated: boolean } {
  const encoded = new TextEncoder().encode(text);
  if (encoded.length <= maxBytes) {
    return { text, truncated: false };
  }
  const decoded = new TextDecoder().decode(encoded.slice(0, maxBytes));
  return { text: decoded.replace(/�+$/, ""), truncated: true };
}

export function floatToBase64Pcm16(pcm: Float32Array): string {
  const pcm16 = new Int16Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) {
    const s = Math.max(-1, Math.min(1, pcm[i]));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(pcm16.buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
